import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import {
  CalendarClock,
  RefreshCw,
  Download,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  X,
  Printer,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ChipBankTabs from '../components/ChipBankTabs';
import ReceiptModal from '../components/ReceiptModal';
import { buildCreditReprintReceipt, REPRINTABLE_CREDIT } from '../components/reprintReceipt';

// ----------------------------------------------------------------
// Player Credit — Aging report
// ----------------------------------------------------------------
// Book-wide FIFO aging across every credit account: one row per player
// with aging buckets, plus portfolio totals. Backed by
// GET /admin/credit/report/aging. Exportable to CSV. credit:view.

const fmtCents = (cents) => {
  if (cents == null) return '$0.00';
  return (Number(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const todayStr = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

function csvEscape(value) {
  const s = String(value == null ? '' : value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const BUCKETS = [
  { key: 'current', label: 'Current' },
  { key: 'd1_30', label: '1–30' },
  { key: 'd31_60', label: '31–60' },
  { key: 'd61_90', label: '61–90' },
  { key: 'd90_plus', label: '90+' },
];

// Extract a sortable value for a row given a column key.
function sortValue(r, key) {
  if (key.startsWith('bucket:')) return Number(r.buckets?.[key.slice(7)] || 0);
  switch (key) {
    case 'name':
      return (r.name || '').toLowerCase();
    case 'memberNumber':
      return r.memberNumber || '';
    case 'status':
      return r.status || '';
    case 'creditLimitCents':
      return Number(r.creditLimitCents || 0);
    case 'balanceCents':
      return Number(r.balanceCents || 0);
    case 'availableCents':
      return Number(r.availableCents || 0);
    case 'oldestDays':
      return Number(r.oldestDays || 0);
    default:
      return 0;
  }
}

export default function CreditAging() {
  const [data, setData] = useState(null); // { rows, totals, generatedAt }
  const [loading, setLoading] = useState(true);
  const [onlyOwing, setOnlyOwing] = useState(false);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('balanceCents');
  const [sortDir, setSortDir] = useState('desc');
  // Click-through: the selected player's account + recent credit activity.
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openPlayer = useCallback(async (row) => {
    if (!row?.userId) return;
    setSelected(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/credit/players/${row.userId}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load player activity');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closePlayer = useCallback(() => {
    setSelected(null);
    setDetail(null);
  }, []);

  // Receipt reprinting: printer settings (best-effort — falls back to the
  // browser dialog) + the receipt currently queued for the print modal.
  const [receiptSettings, setReceiptSettings] = useState({ copies: 2, printerHost: '' });
  const [reprint, setReprint] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/cage/receipt-printing/settings');
        setReceiptSettings({
          copies: res.data.copies || 2,
          printerHost: res.data.printerHost || '',
        });
      } catch {
        /* no cage:view or endpoint down — reprints use the browser dialog */
      }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/credit/report/aging', {
        params: { onlyOwing: onlyOwing ? 'true' : 'false' },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load aging report');
    } finally {
      setLoading(false);
    }
  }, [onlyOwing]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = data?.rows || [];
  const totals = data?.totals || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = rows;
    if (q) {
      out = rows.filter(
        (r) =>
          (r.name || '').toLowerCase().includes(q) ||
          (r.email || '').toLowerCase().includes(q) ||
          String(r.memberNumber || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, query, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'memberNumber' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown size={12} className="inline opacity-40" />;
    return sortDir === 'asc' ? (
      <ArrowUp size={12} className="inline" />
    ) : (
      <ArrowDown size={12} className="inline" />
    );
  };

  const exportCsv = () => {
    const header = [
      'Player',
      'Member #',
      'Email',
      'Phone',
      'Status',
      'Credit Enabled',
      'Limit',
      'Balance Owed',
      'Available',
      'Over Limit',
      'Current',
      '1-30',
      '31-60',
      '61-90',
      '90+',
      'Oldest (days)',
    ];
    const lines = [header.map(csvEscape).join(',')];
    for (const r of filtered) {
      lines.push(
        [
          r.name,
          r.memberNumber,
          r.email,
          r.phone,
          r.status,
          r.creditEnabled ? 'yes' : 'no',
          (Number(r.creditLimitCents) / 100).toFixed(2),
          (Number(r.balanceCents) / 100).toFixed(2),
          (Number(r.availableCents) / 100).toFixed(2),
          r.overLimit ? 'yes' : 'no',
          (Number(r.buckets.current) / 100).toFixed(2),
          (Number(r.buckets.d1_30) / 100).toFixed(2),
          (Number(r.buckets.d31_60) / 100).toFixed(2),
          (Number(r.buckets.d61_90) / 100).toFixed(2),
          (Number(r.buckets.d90_plus) / 100).toFixed(2),
          r.oldestDays,
        ]
          .map(csvEscape)
          .join(',')
      );
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-aging-${todayStr()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <ChipBankTabs />

      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <CalendarClock size={22} className="text-amber-400" /> Player Credit — Aging
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Outstanding house credit aged FIFO by days outstanding.
            {data?.generatedAt && (
              <span className="text-gray-500"> · as of {fmtDateTime(data.generatedAt)}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-300 mr-1 select-none">
            <input
              type="checkbox"
              checked={onlyOwing}
              onChange={(e) => setOnlyOwing(e.target.checked)}
              className="accent-amber-500"
            />
            Only players who owe
          </label>
          <button
            onClick={fetchData}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={exportCsv}
            disabled={!filtered.length}
            className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Portfolio summary */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
          <SummaryCard label="Total outstanding" value={fmtCents(totals.totalOwedCents)} accent />
          <SummaryCard
            label="Players owing"
            value={`${totals.owingCount} / ${totals.accountCount}`}
          />
          <SummaryCard label="Total limit" value={fmtCents(totals.totalLimitCents)} />
          <SummaryCard label="Available" value={fmtCents(totals.totalAvailableCents)} />
          {BUCKETS.map((b) => {
            const hot = b.key === 'd61_90' || b.key === 'd90_plus';
            return (
              <SummaryCard
                key={b.key}
                label={b.label}
                value={fmtCents(totals.buckets[b.key])}
                danger={hot && Number(totals.buckets[b.key]) > 0}
              />
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-3 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by name, member #, email…"
          className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
        />
      </div>

      <p className="text-xs text-gray-500 mb-2">
        Tip: click a player to see their recent credit activity.
      </p>

      {/* Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700 bg-gray-900/50">
                <Th onClick={() => toggleSort('name')}>
                  Player <SortIcon col="name" />
                </Th>
                <Th onClick={() => toggleSort('memberNumber')}>
                  Member # <SortIcon col="memberNumber" />
                </Th>
                <Th onClick={() => toggleSort('status')}>
                  Status <SortIcon col="status" />
                </Th>
                <Th right onClick={() => toggleSort('creditLimitCents')}>
                  Limit <SortIcon col="creditLimitCents" />
                </Th>
                <Th right onClick={() => toggleSort('balanceCents')}>
                  Owed <SortIcon col="balanceCents" />
                </Th>
                <Th right onClick={() => toggleSort('availableCents')}>
                  Available <SortIcon col="availableCents" />
                </Th>
                {BUCKETS.map((b) => (
                  <Th key={b.key} right onClick={() => toggleSort(`bucket:${b.key}`)}>
                    {b.label} <SortIcon col={`bucket:${b.key}`} />
                  </Th>
                ))}
                <Th right onClick={() => toggleSort('oldestDays')}>
                  Oldest <SortIcon col="oldestDays" />
                </Th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-400">
                    <RefreshCw className="animate-spin inline mr-2" size={16} /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                    No credit accounts to show.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr
                    key={r.creditAccountId}
                    onClick={() => openPlayer(r)}
                    className="border-b border-gray-800 last:border-0 hover:bg-gray-900/40 cursor-pointer"
                  >
                    <td className="px-4 py-2.5 text-white">
                      <div className="flex items-center gap-2">
                        {r.name}
                        {r.overLimit && (
                          <span title="Over limit">
                            <AlertTriangle size={13} className="text-red-400" />
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{r.email}</div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-300">{r.memberNumber || '—'}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={r.status} enabled={r.creditEnabled} />
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-300">
                      {fmtCents(r.creditLimitCents)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-amber-400">
                      {fmtCents(r.balanceCents)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-300">
                      {fmtCents(r.availableCents)}
                    </td>
                    {BUCKETS.map((b) => {
                      const v = Number(r.buckets[b.key] || 0);
                      const hot = b.key === 'd61_90' || b.key === 'd90_plus';
                      return (
                        <td
                          key={b.key}
                          className={`px-4 py-2.5 text-right font-mono ${
                            v > 0 ? (hot ? 'text-red-400' : 'text-gray-200') : 'text-gray-600'
                          }`}
                        >
                          {v > 0 ? fmtCents(v) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 text-right text-gray-300">
                      {r.balanceCents > 0 ? `${r.oldestDays}d` : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {totals && filtered.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-700 bg-gray-900/60 font-medium text-white">
                  <td className="px-4 py-2.5" colSpan={3}>
                    Totals ({filtered.length} shown)
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    {fmtCents(sumFiltered(filtered, 'creditLimitCents'))}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-amber-400">
                    {fmtCents(sumFiltered(filtered, 'balanceCents'))}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    {fmtCents(sumFiltered(filtered, 'availableCents'))}
                  </td>
                  {BUCKETS.map((b) => (
                    <td key={b.key} className="px-4 py-2.5 text-right font-mono">
                      {fmtCents(sumBucket(filtered, b.key))}
                    </td>
                  ))}
                  <td className="px-4 py-2.5" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {selected && (
        <CreditActivityModal
          row={selected}
          detail={detail}
          loading={detailLoading}
          onClose={closePlayer}
          onReprint={(r) => setReprint(r)}
        />
      )}

      {reprint && (
        <ReceiptModal
          receipt={reprint}
          copies={receiptSettings.copies}
          autoPrint={false}
          printerHost={receiptSettings.printerHost}
          onClose={() => setReprint(null)}
        />
      )}
    </div>
  );
}

function labelForCreditEntryType(type) {
  switch (type) {
    case 'draw':
      return 'Credit draw';
    case 'payment':
      return 'Payment';
    case 'adjustment_increase':
      return 'Adjustment (increase)';
    case 'adjustment_decrease':
      return 'Adjustment (decrease)';
    case 'writeoff':
      return 'Write-off';
    case 'reversal_increase':
    case 'reversal_decrease':
      return 'Void / reversal';
    default:
      return type;
  }
}

function MiniStat({ label, value, accent }) {
  return (
    <div
      className={`rounded-lg p-3 border ${
        accent ? 'bg-amber-500/5 border-amber-500/30' : 'bg-gray-900/50 border-gray-700'
      }`}
    >
      <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-base font-mono ${accent ? 'text-amber-400' : 'text-white'}`}>
        {value}
      </div>
    </div>
  );
}

// Per-player drill-down: account summary, aging strip, and recent credit
// activity. Data comes from GET /admin/credit/players/:userId (last 25 txns);
// falls back to the aging row's figures while the detail request is in flight.
function CreditActivityModal({ row, detail, loading, onClose, onReprint }) {
  const acct = detail?.account || {};
  const owed = acct.balanceCents ?? row.balanceCents;
  const available = acct.availableCents ?? row.availableCents;
  const limit = acct.creditLimitCents ?? row.creditLimitCents;
  const aging = detail?.aging || { buckets: row.buckets, oldestDays: row.oldestDays };
  const baseItems = detail?.transactions?.items || [];
  const [extraItems, setExtraItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    setExtraItems([]);
    setCursor(detail?.transactions?.nextCursor || null);
  }, [detail]);
  const items = [...baseItems, ...extraItems];
  const loadMore = async () => {
    if (!cursor || loadingMore || !row?.userId) return;
    setLoadingMore(true);
    try {
      const res = await api.get(`/admin/credit/players/${row.userId}/transactions`, {
        params: { cursor, limit: 50 },
      });
      setExtraItems((prev) => [...prev, ...(res.data?.items || [])]);
      setCursor(res.data?.nextCursor || null);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load more activity');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-white">{row.name}</h3>
            <div className="text-xs text-gray-400">
              {row.memberNumber ? `#${row.memberNumber}` : ''}
              {row.email ? `${row.memberNumber ? ' · ' : ''}${row.email}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 p-5 pb-3">
          <MiniStat label="Balance owed" value={fmtCents(owed)} accent />
          <MiniStat label="Available" value={fmtCents(available)} />
          <MiniStat label="Limit" value={fmtCents(limit)} />
        </div>

        {owed > 0 && (
          <div className="px-5 pb-2">
            <div className="grid grid-cols-5 gap-2">
              {BUCKETS.map((b) => {
                const v = Number(aging.buckets?.[b.key] || 0);
                const hot = b.key === 'd61_90' || b.key === 'd90_plus';
                return (
                  <div key={b.key} className="text-center bg-gray-900/50 rounded p-2">
                    <div className="text-[10px] text-gray-500">{b.label}</div>
                    <div
                      className={`text-xs font-mono ${
                        v > 0 ? (hot ? 'text-red-400' : 'text-gray-200') : 'text-gray-600'
                      }`}
                    >
                      {v > 0 ? fmtCents(v) : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="px-5 pt-2 pb-1 text-[11px] uppercase tracking-wide text-gray-500">
          Recent activity
        </div>
        <div className="overflow-y-auto px-5 pb-5">
          {loading ? (
            <div className="py-8 text-center text-gray-400">
              <RefreshCw className="animate-spin inline mr-2" size={16} /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No credit activity yet.</div>
          ) : (
            <div className="divide-y divide-gray-800">
              {items.map((tx) => {
                const up = tx.direction === 'increase'; // owed went up
                return (
                  <div key={tx.id} className="flex items-center justify-between py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm text-white">
                        {labelForCreditEntryType(tx.entryType)}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {fmtDateTime(tx.createdAt)}
                        {tx.banker?.name ? ` · ${tx.banker.name}` : ''}
                        {tx.note ? ` · ${tx.note}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pl-3 flex-shrink-0">
                      {REPRINTABLE_CREDIT.has(tx.entryType) && (
                        <button
                          onClick={() =>
                            onReprint?.(
                              buildCreditReprintReceipt(tx, {
                                playerName: row.name,
                                memberNumber: row.memberNumber,
                                creditLimitCents:
                                  detail?.account?.creditLimitCents ?? row.creditLimitCents,
                              })
                            )
                          }
                          className="text-gray-600 hover:text-amber-400"
                          title="Reprint receipt"
                        >
                          <Printer size={15} />
                        </button>
                      )}
                      <div className="text-right">
                        <div
                          className={`text-sm font-mono ${up ? 'text-red-400' : 'text-emerald-400'}`}
                        >
                          {up ? '+' : '−'}
                          {fmtCents(tx.amountCents)}
                        </div>
                        <div className="text-[11px] text-gray-500 font-mono">
                          owed → {fmtCents(tx.balanceAfterCents)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {cursor && !loading && (
            <div className="pt-3 text-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load older activity'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function sumFiltered(rows, key) {
  return rows.reduce((s, r) => s + Number(r[key] || 0), 0);
}
function sumBucket(rows, key) {
  return rows.reduce((s, r) => s + Number(r.buckets?.[key] || 0), 0);
}

function Th({ children, onClick, right }) {
  return (
    <th
      onClick={onClick}
      className={`px-4 py-3 font-medium whitespace-nowrap cursor-pointer hover:text-gray-200 select-none ${
        right ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function SummaryCard({ label, value, accent, danger }) {
  return (
    <div
      className={`rounded-xl p-3 border ${
        danger
          ? 'bg-red-500/5 border-red-500/30'
          : accent
            ? 'bg-amber-500/5 border-amber-500/30'
            : 'bg-gray-800 border-gray-700'
      }`}
    >
      <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">{label}</div>
      <div
        className={`text-lg font-mono ${
          danger ? 'text-red-400' : accent ? 'text-amber-400' : 'text-white'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status, enabled }) {
  if (!enabled) {
    return (
      <span className="px-2 py-0.5 rounded text-xs bg-gray-600 text-gray-300">disabled</span>
    );
  }
  const cls =
    status === 'active'
      ? 'bg-emerald-500/10 text-emerald-400'
      : status === 'suspended'
        ? 'bg-amber-500/10 text-amber-400'
        : 'bg-gray-600 text-gray-300';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status}</span>;
}
